import {_serializeSingle, _serializeExcept, _serializeExceptList, _serializeList} from '../core';
import type {ServerData, ServerRecord} from '../core';
import type {DomainOf} from '../../../../types/domain';
import type {WithDocumentLabels, WithDeletedParticipant, WithDepartment} from '../_extras';
import {serializeDepartment, serializeDeletedParticipant} from '../member';
import {
    serializeDocumentLabelList,
    serializeDocumentRequesterBff,
    serializeDocumentApproverBff,
    serializeDocumentRevokerBff,
    serializeDocumentSealApproverBff,
    serializeDocumentSealManagerBff,
} from './participant';
import {serializeDocumentSealBff} from './seal';
import {serializeDocumentAttachmentList} from './attachment';

/** 스키마 밖에서 조립되는 관계 필드 — 서버 응답 계약의 일부. */
type DocumentBffExtras = WithDocumentLabels & WithDeletedParticipant & WithDepartment & {
    documentRequester?: unknown;
    documentApprovers?: unknown[];
    documentSeals?: unknown[];
    documentRevoker?: unknown;
    documentSealApprovers?: unknown[];
    documentSealManagers?: unknown[];
    documentAttachments?: unknown[];
    department?: unknown;
    documentInstanceSummary?: DocumentInstanceSummary;
};

/** 인스턴스 집계 — 서버가 문서 BFF 응답에 조립 */
export type DocumentInstanceSummary = {
    /** 대량 여부 — document.type 게이트 대체 */
    bulk: boolean;
    totalCount: number;
    representativeId: string;
    representativeName: string;
    completeCount: number;
    failureCount: number;
    progressCount: number;
};

export function serializeDocument(data: ServerData) {
    return _serializeSingle("document", data);
}

export function serializeDocumentList(data: ServerData) {
    return _serializeList("document", data);
}

/**
 * system 평문 응답이 싣지 않는 document 필드
 * - system document 응답에는 attribute 자체가 없어 전부 제외
 */
const SYSTEM_DOCUMENT_OMIT = [
    "form", "serverForm", "departmentId",
    "attribute.imported", "attribute.autoCompleted", "attribute.enabledInterPageSeal",
    "attribute.enabledViewPassword", "attribute.viewPassword", "attribute.viewPasswordHint",
    "attribute.enabledViewPasswordOnComplete", "attribute.enabledApproverFullApproval",
    "attribute.enabledExternalDocument", "attribute.externalDocumentReason",
    "attribute.enabledManualSealDate", "attribute.manualSealDate",
] as const;

/**
 * system BFF 응답이 싣지 않는 document 필드
 * - system document bff 응답에는 attribute 필드를 주고, 없는 viewPassword 계열 필드만 제외
 */
const SYSTEM_DOCUMENT_BFF_OMIT = [
    "form", "serverForm", "departmentId",
    "attribute.enabledViewPassword", "attribute.viewPassword",
    "attribute.viewPasswordHint", "attribute.enabledViewPasswordOnComplete",
] as const;

export function serializeDocumentSystem(data: ServerData) {
    return _serializeExcept("document", data, SYSTEM_DOCUMENT_OMIT);
}

/** system BFF 목록·검색용 */
export function serializeDocumentSystemBff(data: ServerData) {
    return _serializeExcept("document", data, SYSTEM_DOCUMENT_BFF_OMIT);
}

export function serializeDocumentSystemList(data: ServerData) {
    return _serializeExceptList("document", data, SYSTEM_DOCUMENT_OMIT);
}

/**
 * @param options.omit 역할별로 응답에 없는 스키마 필드
 */
export function serializeDocumentBff(data: ServerData, options: {omit?: readonly string[]} = {}) {
    if (!data) return null;
    const {omit} = options;
    const doc: DomainOf<"document"> & DocumentBffExtras = omit
        ? _serializeExcept("document", data, omit) as DomainOf<"document"> & DocumentBffExtras
        : _serializeSingle("document", data);

    // 서버 응답 필드명: documentLabels (not labels)
    if (data.documentLabels) {
        doc.documentLabels = serializeDocumentLabelList(data.documentLabels);
    }

    // 서버 응답 필드명: documentRequester (not requester)
    // documentRequester는 member 정보를 플랫하게 포함
    if (data.documentRequester) {
        doc.documentRequester = serializeDocumentRequesterBff(data.documentRequester);
    }

    // 서버 응답 필드명: documentApprovers (not approvers)
    // documentApprovers는 member 정보를 플랫하게 포함
    if (data.documentApprovers) {
        doc.documentApprovers = data.documentApprovers.map((a: ServerRecord) => serializeDocumentApproverBff(a));
    }

    // 서버 응답 필드명: documentSeals (not seals)
    // 각 documentSeal 은 자체 documentLabels 배열을 포함하므로 BFF 직렬화 필요
    if (data.documentSeals) {
        doc.documentSeals = data.documentSeals.map((ds: ServerRecord) => serializeDocumentSealBff(ds));
    }

    // 서버 응답 필드명: documentRevoker
    if (data.documentRevoker) {
        doc.documentRevoker = serializeDocumentRevokerBff(data.documentRevoker);
    }

    // 서버 응답 필드명: documentSealApprovers
    if (data.documentSealApprovers) {
        doc.documentSealApprovers = data.documentSealApprovers.map((sa: ServerRecord) => serializeDocumentSealApproverBff(sa));
    }

    // 서버 응답 필드명: documentSealManagers
    if (data.documentSealManagers) {
        doc.documentSealManagers = data.documentSealManagers.map((sm: ServerRecord) => serializeDocumentSealManagerBff(sm));
    }

    // 인스턴스 집계
    if (data.documentInstanceSummary) {
        doc.documentInstanceSummary = data.documentInstanceSummary as DocumentInstanceSummary;
    }

    // 서버 응답 필드명: documentAttachments
    if (data.documentAttachments) {
        doc.documentAttachments = serializeDocumentAttachmentList(data.documentAttachments);
    }

    // 문서 귀속 부서 (서버: department = 단일 DepartmentInternalWebResponse, 작성 시점 현재 부서). null = 개인.
    if (data.department) doc.department = serializeDepartment(data.department);

    // 삭제자 스냅샷
    if (data.deletedParticipant) doc.deletedParticipant = serializeDeletedParticipant(data.deletedParticipant);

    return doc;
}

/** 목록·검색 응답이 싣지 않는 필드 — 본문 form 은 상세에만 */
const DOCUMENT_SUMMARY_OMIT = ["form"] as const;

export function serializeDocumentBffSummary(data: ServerData) {
    return serializeDocumentBff(data, {omit: DOCUMENT_SUMMARY_OMIT});
}

export function serializeDocumentBffSummaryList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentBffSummary(item));
}

/** system BFF 상세 */
export function serializeDocumentBffSystem(data: ServerData) {
    return serializeDocumentBff(data, {omit: SYSTEM_DOCUMENT_BFF_OMIT});
}
